import { GEAR_PREFERENCES_LOCAL_STORAGE_KEY } from '@/lib/config';
import {
  AUTO_PERSPECTIVE,
  DEFAULT_PERSPECTIVE,
  MAX_MRU_PERSPECTIVES,
} from '@/types/perspectiveTypes';
import {
  DEFAULT_THINKING_MODE,
  type ThinkingMode,
} from '@/types/thinkingTypes';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * UI Settings Redux Slice with localStorage Persistence
 *
 * This slice manages UI settings including:
 * - Thinking mode selection (fast/thinking/deep)
 * - Gear options (markdown)
 *
 * Key Features:
 * - Automatic localStorage persistence on every state change
 * - Initialization from localStorage on app startup
 * - SSR-safe (handles window undefined)
 * - Error handling for localStorage operations
 * - Migration from old boolean thinking state to new ThinkingMode
 *
 * localStorage Integration:
 * - Initial state is loaded from localStorage using getStoredGearOptions()
 * - Every reducer action calls saveGearOptions() to persist changes
 * - Uses the same GEAR_PREFERENCES_LOCAL_STORAGE_KEY for consistency
 * - Graceful fallback to defaults if localStorage fails
 */

export interface GearOption {
  id: string;
  label: string;
  enabled: boolean;
  tooltip?: string;
}

export type ReferenceType = 'int' | 'ext' | 'none';

export type ThemeMode = 'system' | 'light' | 'dark';

export interface UiSettingsState {
  thinkingMode: ThinkingMode;
  perspectiveLens: string;
  perspectiveMru: string[];
  gearOptions: GearOption[];
  referenceType: ReferenceType;
  themeMode: ThemeMode;
  // Whether the in-shell Customize sheet is open (#5238). Hoisted out of
  // SidebarActionList's local state so it survives that component unmounting
  // when the mobile sidebar drawer collapses — the sheet is rendered once at
  // the shell root (MainContentArea) and driven from here. Ephemeral session
  // state: deliberately NOT persisted to localStorage (a sheet re-opening
  // itself on reload would be surprising).
  customizeSheetOpen: boolean;
  // Selected context-chip ids (Spisy, #6269) threaded into the research request.
  // Ephemeral session state — deliberately NOT persisted (chip ids are per-user
  // and meaningless across reloads/accounts; the composer re-defaults to the
  // Storage chip on load).
  selectedContextChipIds: number[];
  // Whether the user has explicitly touched the context-chip selection (#6348).
  // Hoisted out of ContextChipPicker's local ref so an explicit clear/choose
  // survives the composer remounting (e.g. the mobile drawer collapsing) — the
  // Storage auto-select effect must not re-fire and override the user. Ephemeral
  // session state: deliberately NOT persisted, same lifetime as the selection.
  contextChipsTouched: boolean;
  // Whether useSessionChipHydration is still resolving the persisted chip
  // scope for the current session (#8064 /ck F1). Kept in lockstep with the
  // hook's local `isHydrating` so the SEND SITES (Research.tsx /
  // useResearchStream) can see it: while pending, a submitted turn must OMIT
  // `context_chips` entirely (tri-state None on the backend) — otherwise a
  // fast follow-up would persist `[]` and durably erase the stored session
  // scope. Ephemeral session state: deliberately NOT persisted, same lifetime
  // as `selectedContextChipIds`/`contextChipsTouched`.
  chipHydrationPending: boolean;
  // The Spis (case) id a chat-entry-point button was clicked from, threaded
  // through to whichever call site actually creates the session (Phase 4
  // claims-memory, D-11). `openCaseChat`/`useChatWithFolder`-style buttons
  // select a context chip and navigate to a blank `/chat` — they never create
  // the session themselves, so this field carries `case_id` across that gap
  // until `Research.tsx`'s `performResearch` reads it, threads it into
  // `sessionServices.getSessionId`, and clears it again. Read-once,
  // deliberately NOT a general "current case" field. Ephemeral session state:
  // deliberately NOT persisted (same lifetime rationale as
  // `selectedContextChipIds`/`contextChipsTouched`).
  pendingSessionCaseId: number | null;
  // Whether the generated-document pane is the one showing on compact
  // (< lg) viewports of a doc-gen chat session (#7198). Below `lg` the chat
  // transcript and the document editor cannot share the row, so exactly one
  // of them is mounted-visible at a time and this flag says which.
  // Hoisted into Redux rather than the page's local state because the
  // DocumentArtifactChip — which is what the user taps to reach the document
  // — is rendered deep inside ResearchSession/ChatMessageItem and has no prop
  // path back to the page (same rationale as `customizeSheetOpen`). Ignored at
  // `lg` and above, where both panes are visible side by side. Ephemeral
  // session state: deliberately NOT persisted, and reset whenever the chat
  // page switches session.
  documentPaneOpen: boolean;
}

// Default gear options - all enabled by default
const defaultGearOptions: GearOption[] = [
  {
    id: 'markdown',
    label: 'Markdown',
    enabled: true,
  },
];

// Helper functions for localStorage persistence
const OLD_THINKING_STORAGE_KEY = 'lexomat_thinking_state'; // Legacy boolean key
const THINKING_MODE_STORAGE_KEY = 'lexomat_thinking_mode'; // New ThinkingMode key
const PERSPECTIVE_LENS_STORAGE_KEY = 'lexomat_perspective_lens';
const PERSPECTIVE_MRU_STORAGE_KEY = 'lexomat_perspective_mru';
const REFERENCES_STORAGE_KEY = 'lexomat_references_state';
// NOTE: Theme is now managed by custom ThemeProvider using 'lexomat_user_theme' key.
// This Redux key is kept for potential future use but is currently unused.
const THEME_MODE_STORAGE_KEY = 'lexomat_redux_theme_mode';
const DEFAULT_THEME_MODE: ThemeMode = 'system';

// One-time migration: remove the old auto-save preference from localStorage now
// that the setting is stored server-side in the user profile.
if (typeof window !== 'undefined') {
  localStorage.removeItem('lexomat_auto_save_chat_uploads');
}

/**
 * Gets the stored thinking mode from localStorage.
 * Handles migration from old boolean format to new ThinkingMode format.
 *
 * Migration logic:
 * - Old `true` (thinking was ON) -> 'thinking'
 * - Old `false` (thinking was OFF) -> 'fast'
 * - New format is used as-is
 * - Default for new users: 'fast'
 */
const getStoredThinkingMode = (): ThinkingMode => {
  if (typeof window === 'undefined') return DEFAULT_THINKING_MODE;

  // Check for new format first
  const newValue = localStorage.getItem(THINKING_MODE_STORAGE_KEY);
  if (newValue) {
    try {
      const parsed = JSON.parse(newValue);
      // Validate it's a valid ThinkingMode
      if (parsed === 'fast' || parsed === 'thinking' || parsed === 'deep') {
        return parsed;
      }
    } catch {
      // Invalid JSON, will fall through to migration or default
    }
  }

  // Check for old boolean format and migrate
  const oldValue = localStorage.getItem(OLD_THINKING_STORAGE_KEY);
  if (oldValue !== null) {
    try {
      const wasThinkingOn = JSON.parse(oldValue);
      const migratedMode: ThinkingMode = wasThinkingOn ? 'thinking' : 'fast';

      // Save in new format and remove old key
      localStorage.setItem(
        THINKING_MODE_STORAGE_KEY,
        JSON.stringify(migratedMode)
      );
      localStorage.removeItem(OLD_THINKING_STORAGE_KEY);

      console.warn(
        `[uiSettingsSlice] Migrated thinking state: ${wasThinkingOn} -> '${migratedMode}'`
      );
      return migratedMode;
    } catch {
      // Invalid old value, remove it
      localStorage.removeItem(OLD_THINKING_STORAGE_KEY);
    }
  }

  return DEFAULT_THINKING_MODE;
};

const saveThinkingMode = (mode: ThinkingMode): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(THINKING_MODE_STORAGE_KEY, JSON.stringify(mode));
};

// Known corrupted values leaked from backend error messages (Issue #1806).
// These may persist in user localStorage from before the backend fix.
const CORRUPTED_PERSPECTIVE_VALUES = [
  'last perspective not found',
  'error updating last perspective',
];

const isCorruptedPerspective = (value: string): boolean =>
  CORRUPTED_PERSPECTIVE_VALUES.some((bad) =>
    value.toLowerCase().startsWith(bad)
  );

// Unset/invalid → AUTO_PERSPECTIVE (visible auto-adaptation). A stored
// 'general' is kept as-is: it is now an explicit opt-out the user chose.
const getStoredPerspectiveLens = (): string => {
  if (typeof window === 'undefined') return AUTO_PERSPECTIVE;
  const stored = localStorage.getItem(PERSPECTIVE_LENS_STORAGE_KEY);
  if (!stored) return AUTO_PERSPECTIVE;
  try {
    const parsed = JSON.parse(stored);
    if (typeof parsed !== 'string' || isCorruptedPerspective(parsed)) {
      localStorage.removeItem(PERSPECTIVE_LENS_STORAGE_KEY);
      return AUTO_PERSPECTIVE;
    }
    return parsed;
  } catch {
    return AUTO_PERSPECTIVE;
  }
};

const savePerspectiveLens = (lens: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PERSPECTIVE_LENS_STORAGE_KEY, JSON.stringify(lens));
};

const getStoredPerspectiveMru = (): string[] => {
  if (typeof window === 'undefined') return [];
  const stored = localStorage.getItem(PERSPECTIVE_MRU_STORAGE_KEY);
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    const cleaned = parsed.filter(
      (v): v is string => typeof v === 'string' && !isCorruptedPerspective(v)
    );
    if (cleaned.length !== parsed.length) {
      localStorage.setItem(
        PERSPECTIVE_MRU_STORAGE_KEY,
        JSON.stringify(cleaned.slice(0, MAX_MRU_PERSPECTIVES))
      );
    }
    return cleaned.slice(0, MAX_MRU_PERSPECTIVES);
  } catch {
    return [];
  }
};

const savePerspectiveMru = (mru: string[]): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    PERSPECTIVE_MRU_STORAGE_KEY,
    JSON.stringify(mru.slice(0, MAX_MRU_PERSPECTIVES))
  );
};

const getStoredReferenceType = (): ReferenceType => {
  if (typeof window === 'undefined') return 'int';
  const stored = localStorage.getItem(REFERENCES_STORAGE_KEY);
  return stored ? JSON.parse(stored) : 'int';
};

const saveReferenceType = (referenceType: ReferenceType): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(REFERENCES_STORAGE_KEY, JSON.stringify(referenceType));
};

/**
 * Gets the stored theme mode from localStorage.
 * Returns 'system' as default if no preference is stored.
 */
const getStoredThemeMode = (): ThemeMode => {
  if (typeof window === 'undefined') return DEFAULT_THEME_MODE;
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return DEFAULT_THEME_MODE;
};

const saveThemeMode = (mode: ThemeMode): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
};

/**
 * Loads gear options from localStorage on app initialization
 * Falls back to default options if localStorage is unavailable or corrupted
 * Also migrates old data by removing lexomatLinks option
 */
const getStoredGearOptions = (): GearOption[] => {
  if (typeof window === 'undefined') return defaultGearOptions;
  try {
    const stored = localStorage.getItem(GEAR_PREFERENCES_LOCAL_STORAGE_KEY);
    if (!stored) return defaultGearOptions;

    const parsedOptions: GearOption[] = JSON.parse(stored);

    // Filter out lexomatLinks option if it exists (migration)
    const filteredOptions = parsedOptions.filter(
      (option) => option.id !== 'lexomatLinks'
    );

    // If we filtered out options, save the cleaned version
    if (filteredOptions.length !== parsedOptions.length) {
      saveGearOptions(filteredOptions);
    }

    return filteredOptions.length > 0 ? filteredOptions : defaultGearOptions;
  } catch (error) {
    console.error('Failed to parse stored gear options:', error);
    return defaultGearOptions;
  }
};

/**
 * Saves gear options to localStorage whenever Redux state changes
 * This ensures user preferences persist across browser sessions
 */
const saveGearOptions = (gearOptions: GearOption[]): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      GEAR_PREFERENCES_LOCAL_STORAGE_KEY,
      JSON.stringify(gearOptions)
    );
  } catch (error) {
    console.error('Failed to save gear options to localStorage:', error);
  }
};

const initialState: UiSettingsState = {
  thinkingMode: getStoredThinkingMode(), // Initialize from localStorage (with migration)
  perspectiveLens: getStoredPerspectiveLens(), // Initialize from localStorage
  perspectiveMru: getStoredPerspectiveMru(), // Initialize from localStorage
  gearOptions: getStoredGearOptions(), // Initialize from localStorage
  referenceType: getStoredReferenceType(), // Initialize from localStorage
  themeMode: getStoredThemeMode(), // Initialize from localStorage
  customizeSheetOpen: false, // Ephemeral session state — never persisted
  selectedContextChipIds: [], // Ephemeral session state — never persisted (#6269)
  contextChipsTouched: false, // Ephemeral session state — never persisted (#6348)
  chipHydrationPending: false, // Ephemeral session state — never persisted (#8064)
  pendingSessionCaseId: null, // Ephemeral session state — never persisted (D-11)
  documentPaneOpen: false, // Ephemeral session state — never persisted (#7198)
};

const uiSettingsSlice = createSlice({
  name: 'uiSettings',
  initialState,
  reducers: {
    /**
     * Sets the thinking mode to a specific value.
     * Replaces the old toggleThinking action.
     */
    setThinkingMode(state, action: PayloadAction<ThinkingMode>) {
      state.thinkingMode = action.payload;
      saveThinkingMode(state.thinkingMode);
    },
    /**
     * Sets the perspective lens (Issue #1799).
     * Also updates MRU list for non-general perspectives.
     */
    setPerspectiveLens(state, action: PayloadAction<string>) {
      if (isCorruptedPerspective(action.payload)) return;
      state.perspectiveLens = action.payload;
      savePerspectiveLens(state.perspectiveLens);
      // Update MRU: add to front, deduplicate, cap at max
      if (
        action.payload !== DEFAULT_PERSPECTIVE &&
        action.payload !== AUTO_PERSPECTIVE
      ) {
        const filtered = state.perspectiveMru.filter(
          (p) => p !== action.payload
        );
        state.perspectiveMru = [action.payload, ...filtered].slice(
          0,
          MAX_MRU_PERSPECTIVES
        );
        savePerspectiveMru(state.perspectiveMru);
      }
    },
    /**
     * @deprecated Use setThinkingMode instead. Kept for backward compatibility during migration.
     * Maps: true -> 'thinking', false -> 'fast'
     */
    setInitialThinking(state, action: PayloadAction<boolean>) {
      // Map boolean to ThinkingMode for backward compatibility
      state.thinkingMode = action.payload ? 'thinking' : 'fast';
      saveThinkingMode(state.thinkingMode);
    },
    toggleGearOption(state, action: PayloadAction<string>) {
      const optionId = action.payload;
      const option = state.gearOptions.find((opt) => opt.id === optionId);
      if (option) {
        option.enabled = !option.enabled;
        saveGearOptions(state.gearOptions); // Save to localStorage
      }
    },
    setGearOption(
      state,
      action: PayloadAction<{ id: string; enabled: boolean }>
    ) {
      const { id, enabled } = action.payload;
      const option = state.gearOptions.find((opt) => opt.id === id);
      if (option) {
        option.enabled = enabled;
        saveGearOptions(state.gearOptions); // Save to localStorage
      }
    },
    setGearOptions(state, action: PayloadAction<GearOption[]>) {
      state.gearOptions = action.payload;
      saveGearOptions(state.gearOptions); // Save to localStorage
    },
    resetGearOptions(state) {
      state.gearOptions = defaultGearOptions;
      saveGearOptions(state.gearOptions); // Save to localStorage
    },
    setReferenceType(state, action: PayloadAction<ReferenceType>) {
      state.referenceType = action.payload;
      saveReferenceType(state.referenceType); // Save to localStorage
    },
    /**
     * Sets the theme mode (system, light, or dark).
     * 'system' follows device prefers-color-scheme setting.
     */
    setThemeMode(state, action: PayloadAction<ThemeMode>) {
      state.themeMode = action.payload;
      saveThemeMode(state.themeMode); // Save to localStorage
    },
    /**
     * Opens/closes the in-shell Customize sheet (#5238). Lives in Redux rather
     * than SidebarActionList local state so the sheet survives the sidebar
     * component unmounting on mobile drawer collapse. Not persisted.
     */
    setCustomizeSheetOpen(state, action: PayloadAction<boolean>) {
      state.customizeSheetOpen = action.payload;
    },
    /**
     * Replace the set of selected context-chip ids (Spisy, #6269). The composer
     * threads these into the research request to scope retrieval. Not persisted.
     */
    setSelectedContextChipIds(state, action: PayloadAction<number[]>) {
      // Dedupe defensively — the picker toggles by id and must stay a set.
      state.selectedContextChipIds = Array.from(new Set(action.payload));
    },
    /**
     * Marks the context-chip selection as user-touched (#6348). Set when the
     * user toggles/clears a chip so the Storage auto-select effect stops firing
     * — and, crucially, *stays* set across composer remounts (Redux outlives the
     * component), unlike the component-local ref it replaces. Not persisted.
     */
    setContextChipsTouched(state, action: PayloadAction<boolean>) {
      state.contextChipsTouched = action.payload;
    },
    /**
     * Mirrors useSessionChipHydration's local `isHydrating` into Redux
     * (#8064 /ck F1) so the research send sites can suppress the
     * `context_chips` payload field while the persisted scope is still being
     * resolved. The hook is the only writer; set true synchronously on mount
     * with a sessionId, false once hydration settles/fails/is absent.
     */
    setChipHydrationPending(state, action: PayloadAction<boolean>) {
      state.chipHydrationPending = action.payload;
    },
    /**
     * Clears the ephemeral context-chip session state on an auth state change
     * (#6376). Sign-out is a soft `router.push('/')` (no reload) and this slice
     * is neither persisted nor otherwise reset on auth change — so without this,
     * an account switch within one SPA session would leak User A's chip
     * selection + touched flag into User B's composer. Dispatched on sign-out
     * AND sign-in (covers the A→B switch) by ContextChipAuthReset.
     */
    resetContextChipsOnAuthChange(state) {
      state.selectedContextChipIds = [];
      state.contextChipsTouched = false;
    },
    /**
     * Sets (or clears, via `null`) the Spis id a chat-entry-point button was
     * clicked from (Phase 4 claims-memory, D-11). Consumed once by
     * `Research.tsx`'s `performResearch`, which threads it into
     * `sessionServices.getSessionId` and clears it back to `null` after a
     * confirmed-successful session creation. Not persisted.
     */
    setPendingSessionCaseId(state, action: PayloadAction<number | null>) {
      state.pendingSessionCaseId = action.payload;
    },
    /**
     * Shows (true) or hides (false) the generated-document pane on compact
     * viewports of a doc-gen chat session (#7198). Dispatched by the compact
     * pane toggle and by DocumentArtifactChip — the chip is what makes the
     * document reachable from a chat message below `lg`, where the editor
     * panel is otherwise not on screen at all. Not persisted.
     */
    setDocumentPaneOpen(state, action: PayloadAction<boolean>) {
      state.documentPaneOpen = action.payload;
    },
  },
});

export const {
  setThinkingMode,
  setPerspectiveLens,
  // @deprecated - use setThinkingMode
  toggleGearOption,

  setReferenceType,
  setCustomizeSheetOpen,
  setSelectedContextChipIds,
  setContextChipsTouched,
  setChipHydrationPending,
  resetContextChipsOnAuthChange,
  setPendingSessionCaseId,
  setDocumentPaneOpen,
} = uiSettingsSlice.actions;

// Re-export types for convenience
export type { ThinkingMode } from '@/types/thinkingTypes';

export default uiSettingsSlice.reducer;
